import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp, 
  Lightning, 
  Target, 
  Warning,
  CheckCircle,
  XCircle,
  ArrowRight,
  TrendUp,
  ChartBar,
  CurrencyBtc,
  Clock,
  Strategy,
  ShieldCheck,
  ListChecks,
  Notebook
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const CryptoTradingJournal = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Best Crypto Trading Journal (2026) | Track Your Trades Effectively',
      description: 'Discover the best crypto trading journal for tracking your trades, improving performance, and staying consistent.',
      path: '/crypto-trading-journal',
      lang: 'en',
      alternates: getArticleAlternates('/crypto-trading-journal'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <header className="max-w-4xl mx-auto px-6 pt-16 pb-12">
        <div className="flex items-center gap-2 text-[#F7931A] text-sm font-medium mb-4">
          <CurrencyBtc size={16} weight="fill" />
          <span>Crypto Trading</span>
        </div>
        
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
          Best Trading Journal for Crypto Traders
          <span className="block text-white/60 text-2xl md:text-3xl mt-2">
            (2026 Guide)
          </span>
        </h1>
        
        <p className="text-lg text-white/70 leading-relaxed max-w-3xl">
          Crypto markets move fast—24/7 volatility, multiple exchanges, and split-second decisions. 
          A <strong className="text-white">crypto trading journal</strong> isn't optional anymore. 
          It's how serious traders separate signal from noise and turn chaos into consistency.
        </p>
      </header>

      <main className="max-w-4xl mx-auto px-6 pb-20">
        
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#F7931A]/10 to-transparent border border-[#F7931A]/20 rounded-lg p-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-3">
              <Lightning size={24} weight="duotone" className="text-[#F7931A]" />
              Why Crypto Requires Better Tracking
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Unlike traditional markets, crypto never sleeps. You're trading across multiple exchanges, 
              dealing with extreme volatility, and making decisions at 3 AM. Without a proper 
              <strong className="text-white"> trading journal for crypto traders</strong>, you're flying blind.
            </p>
            <p className="text-white/70 leading-relaxed">
              The traders who survive long-term aren't the ones with the best instincts—they're the ones 
              who track everything, learn from their data, and eliminate what doesn't work. Using an 
              <Link to="/automated-trading-journal" className="text-[#00E676] hover:underline"> automated trading journal</Link> makes 
              this process significantly easier.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Warning size={28} weight="duotone" className="text-red-400" />
            Why Crypto Traders Struggle Without a Journal
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "No Consistent Tracking",
                description: "Trading across Binance, Coinbase, Bybit, and DEXs? Good luck remembering every entry and exit without documentation.",
                icon: XCircle
              },
              {
                title: "Emotional Decision Making",
                description: "FOMO buys, panic sells, revenge trades. Without data showing your patterns, emotions run the show.",
                icon: Warning
              },
              {
                title: "Can't Identify What Works",
                description: "You had a great month—but which setups actually made money? Which tokens? Which timeframes?",
                icon: ChartBar
              },
              {
                title: "Repeating the Same Mistakes",
                description: "Without reviewing your trades, you keep making the same errors. The market keeps taking your money.",
                icon: Clock
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-6 hover:border-red-400/30 transition-colors"
              >
                <item.icon size={24} weight="duotone" className="text-red-400 mb-3" />
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <ListChecks size={28} weight="duotone" className="text-[#00E676]" />
            What to Track in Your Crypto Trades
          </h2>
          
          <p className="text-white/70 mb-8 leading-relaxed">
            A proper <strong className="text-white">crypto trading journal</strong> captures more than 
            just entry and exit prices. Here's what matters:
          </p>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
              {[
                { item: "Entry & Exit Price", detail: "Know your exact execution" },
                { item: "Position Size", detail: "Track risk per trade" },
                { item: "Risk/Reward Ratio", detail: "Measure trade quality" },
                { item: "Setup Type", detail: "Categorize by strategy" },
                { item: "Timeframe", detail: "5M, 1H, 4H, Daily" },
                { item: "Outcome & Notes", detail: "Learn from every trade" }
              ].map((track, index) => (
                <div key={index} className="p-5 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle size={16} weight="fill" className="text-[#00E676]" />
                    <span className="font-medium text-sm">{track.item}</span>
                  </div>
                  <p className="text-white/50 text-xs pl-6">{track.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <XCircle size={28} weight="duotone" className="text-orange-400" />
            Common Mistakes Crypto Traders Make
          </h2>
          
          <div className="space-y-4">
            {[
              {
                mistake: "Overtrading",
                explanation: "Taking 20+ trades a day without tracking any of them. Quantity doesn't equal quality.",
                solution: "Track every trade. You'll naturally reduce low-quality setups."
              },
              {
                mistake: "Ignoring Performance Metrics",
                explanation: "Not knowing your win rate, average R:R, or profit factor. You're gambling, not trading.",
                solution: "Use a journal that calculates these automatically."
              },
              {
                mistake: "No Post-Trade Review",
                explanation: "Closing a trade and moving on without learning anything from it.",
                solution: "Add notes to every trade. Review weekly. Learn how to analyze your trades properly."
              },
              {
                mistake: "Inconsistent Documentation",
                explanation: "Journaling only when you feel like it. Missing data = incomplete picture.",
                solution: "Automate your tracking so every trade is captured."
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-6 hover:border-orange-400/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-orange-400/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-orange-400 font-bold text-sm">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-orange-400">{item.mistake}</h3>
                    <p className="text-white/60 text-sm mb-2">{item.explanation}</p>
                    <p className="text-white/80 text-sm flex items-center gap-2">
                      <CheckCircle size={14} weight="fill" className="text-[#00E676]" />
                      {item.solution}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <ShieldCheck size={28} weight="duotone" className="text-[#00E676]" />
            What a Good Crypto Trading Journal Should Have
          </h2>
          
          <p className="text-white/70 mb-8 leading-relaxed">
            Not all journals are built for crypto. Here's what to look for when choosing 
            a <strong className="text-white">trading journal for crypto traders</strong>:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                feature: "Trade Automation",
                description: "Manual entry is slow and error-prone. Look for paste-to-log or chart capture features.",
                icon: Lightning
              },
              {
                feature: "Real-Time Analytics",
                description: "Win rate, profit factor, average R:R—calculated automatically with every trade.",
                icon: ChartLineUp
              },
              {
                feature: "Setup Categorization",
                description: "Tag trades by strategy (breakout, scalp, reversal) to see what actually works.",
                icon: Strategy
              },
              {
                feature: "Partial Execution Tracking",
                description: "Crypto traders scale in and out. Your journal should handle multiple entries and exits.",
                icon: Target
              },
              {
                feature: "Performance History",
                description: "Calendar views, equity curves, and trend analysis to track progress over time.",
                icon: TrendUp
              },
              {
                feature: "Multi-Asset Support",
                description: "BTC, ETH, altcoins, and more—all in one place without switching tools.",
                icon: CurrencyBtc
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-white/10 rounded-lg p-6 hover:border-[#00E676]/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={24} weight="duotone" className="text-[#00E676]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.feature}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            The Bottom Line
          </h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed mb-4">
              If you're serious about crypto trading, you need to <strong className="text-white">track your crypto trades</strong> consistently. 
              The market doesn't reward hope—it rewards preparation, discipline, and continuous improvement.
            </p>
            <p className="text-white/70 leading-relaxed">
              A good <strong className="text-white">crypto trading journal</strong> isn't just a log of your trades. 
              It's a feedback system that shows you exactly where you're winning, where you're losing, 
              and what to do about it.
            </p>
          </div>
          
          <div className="mt-6 p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm">
              <strong>Related:</strong>{' '}
              <Link to="/automated-trading-journal" className="text-[#00E676] hover:underline">
                What Is an Automated Trading Journal? →
              </Link>
            </p>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-16">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/how-to-analyze-your-trades" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Analyze Your Trades</h3>
              <p className="text-white/50 text-sm">Step-by-step guide to reviewing trades like a pro.</p>
            </Link>
            <Link to="/how-to-find-a-trading-setup" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Find a Trading Setup</h3>
              <p className="text-white/50 text-sm">Discover what makes a profitable trading setup.</p>
            </Link>
            <Link to="/why-traders-fail-to-improve" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Why Traders Fail to Improve</h3>
              <p className="text-white/50 text-sm">Common mistakes that keep traders stuck in place.</p>
            </Link>
            <Link to="/manual-vs-automated-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Manual vs Automated Journals</h3>
              <p className="text-white/50 text-sm">Which journaling method is right for you?</p>
            </Link>
          </div>
        </section>

        <section className="mb-8">
          <div className="bg-gradient-to-br from-[#F7931A]/10 via-[#121212] to-[#00E676]/10 border border-[#F7931A]/30 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <Notebook size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Track Your Crypto Trades?
            </h2>
            
            <p className="text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
              <strong className="text-white">GENI</strong> is a modern trading journal built for crypto traders 
              who need automation and clarity. Paste your chart, get instant analysis, and finally understand 
              what's working in your trading.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Tracking Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "Best Trading Journal for Crypto Traders (2026 Guide)",
              "description": "Discover the best crypto trading journal for tracking your trades, improving performance, and staying consistent.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0]
            })
          }}
        />
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CryptoTradingJournal;
